import { FC, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Formik } from 'formik';
import { type FormikHelpers } from 'formik';
import { EMAIL_FORM, EMAIL_SCHEMA } from 'modules/account/contact/forms/schema';
import { useGetLead } from 'modules/account/contact/hooks';
import { useGmailSendMail } from 'modules/account/inbox/hooks';
import constants from 'modules/account/inbox/utils/constants';
import { useParams } from 'react-router-dom';
import ComposerEmailForm from 'ui/partials/Composer/ComposerEmailForm';
import { GmailThreads, Token } from 'utils/types';

type GmailComposerEmailProps = {
  token: Token;
  attachments: File[];
  setAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  closeComposer: () => void;
  setThreads?: React.Dispatch<React.SetStateAction<GmailThreads>>;
};

const GmailComposerEmail: FC<GmailComposerEmailProps> = ({
  token,
  attachments,
  closeComposer,
  setAttachments,
  setThreads,
}) => {
  const queryClient = useQueryClient();
  const params = useParams();
  const { data } = useGetLead(params?.id as string);

  const { mutate: sendMail } = useGmailSendMail();

  const defaultValue = useMemo(() => {
    if (!data) return null;
    return {
      label: data?.fullName ?? `${data?.firstName} ${data?.lastName}`,
      value: data?.emailAddress,
    };
  }, [data]);

  const from = token.meta?.emailAddress;

  const initialValues: EMAIL_FORM = {
    to: defaultValue ? [defaultValue] : [],
    from,
    cc: [],
    bcc: [],
    subject: '',
    text: '',
  };
  const filetoBase64 = (file: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const onSubmit = async (
    value: EMAIL_FORM,
    { setSubmitting, resetForm }: FormikHelpers<EMAIL_FORM>,
  ) => {
    const castValues = EMAIL_SCHEMA.cast(value);

    const mappedTo = castValues.to.map((val) => val.value);
    const mappedCC = castValues?.cc?.map?.((val) => val.value) ?? [];
    const mappedBCC = castValues?.bcc?.map?.((val) => val.value) ?? [];
    const payload = {
      ...castValues,
      text: castValues?.text?.replace(
        /<p(\b[^>]*)>/g,
        '<p$1 style="margin:0;">',
      ),
      to: mappedTo,
      cc: mappedCC,
      bcc: mappedBCC,
      attachments: await Promise.all(
        attachments?.map(async (attachment: File) => {
          const base64 = await filetoBase64(attachment);
          return {
            filename: attachment.name,
            path: base64,
          };
        }),
      ),
    };

    await sendMail(payload, {
      onSuccess: () => {
        setSubmitting(false);
        closeComposer();
        setThreads?.([]);
        resetForm();
        queryClient.invalidateQueries({
          queryKey: [constants.GMAIL_THREADS],
        });
      },
    });
  };

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={EMAIL_SCHEMA}
    >
      {() => (
        <ComposerEmailForm
          attachments={attachments}
          setAttachments={setAttachments}
        />
      )}
    </Formik>
  );
};

export default GmailComposerEmail;
